import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import StatusButtonGroup from "components/Common/StatusButtonGroup"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateAccountPaymentMethod,
  useEditAccountPaymentMethod,
} from "hooks/useAccountPaymentMethod"

const ACTIVE_STATUS_OPTIONS = [
  { value: true, label: "Active", color: "success", icon: "bx-check-circle" },
  { value: false, label: "Inactive", color: "secondary", icon: "bx-x-circle" },
]

const AccountPaymentMethodAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
  onSuccess,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountPaymentMethod()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountPaymentMethod()

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter payment method name"),
    code: Yup.string().nullable(),
    is_active: Yup.boolean().required(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        name: values.name?.trim(),
        code: values.code?.trim() || null,
        is_active: !!values.is_active,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(
          res?.message ??
            `Payment method ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        onSuccess?.(res?.data)
        toggleModal()
      } catch (error) {
        toast.error("Failed to save payment method.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, onSuccess]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      code: editedData?.code || "",
      is_active: editedData?.is_active ?? true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Payment Method"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Payment Method" : "Add Payment Method"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">Name</Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="code">Code</Label>
          <InputField
            type="text"
            name="code"
            id="code"
            value={formik.values.code}
            onChange={formik.handleChange}
            invalid={formik.touched.code && !!formik.errors.code}
          />
          {formik.touched.code && formik.errors.code && (
            <FormFeedback type="invalid">{formik.errors.code}</FormFeedback>
          )}
        </div>

        <StatusButtonGroup
          name="is_active"
          label="Status"
          value={formik.values.is_active}
          onChange={value => formik.setFieldValue("is_active", value)}
          options={ACTIVE_STATUS_OPTIONS}
        />

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountPaymentMethodAddEdit
